// EP-135 Tier 1 — Resolution create modal. Admin+ only; the page
// checks role before opening. Nested under a meeting.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button, Input, Select, Textarea, Checkbox } from '@/components/ui'
import { useCreateResolution } from '@/hooks/useGovernance'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { ResolutionDecidedBy } from '@/types/governance'

export interface ResolutionFormModalProps {
  isOpen: boolean
  meetingId: number
  onClose: () => void
}

export function ResolutionFormModal({ isOpen, meetingId, onClose }: ResolutionFormModalProps) {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const [seq, setSeq] = useState(1)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [decidedBy, setDecidedBy] = useState<ResolutionDecidedBy>('vote')
  const [secret, setSecret] = useState(false)

  const create = useCreateResolution(meetingId)

  const handleSave = async () => {
    if (!title.trim() || seq < 1) return
    try {
      await create.mutateAsync({
        seq,
        title: title.trim(),
        body: body.trim() || null,
        decided_by: decidedBy,
        secret,
      })
      setSeq(1)
      setTitle('')
      setBody('')
      setDecidedBy('vote')
      setSecret(false)
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('meetings.actions.add_resolution')}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!title.trim() || seq < 1}
            loading={create.isPending}
            data-testid="btn-save-resolution"
          >
            {tc('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('resolutions.labels.seq')}
          type="number"
          min={1}
          value={String(seq)}
          onChange={(e) => setSeq(Number(e.target.value))}
          required
        />
        <Input
          label={t('resolutions.labels.title')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          data-testid="input-resolution-title"
        />
        <Textarea
          label={t('resolutions.labels.body')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
        />
        <Select
          label={t('resolutions.labels.decided_by')}
          value={decidedBy}
          onChange={(e) => setDecidedBy(e.target.value as ResolutionDecidedBy)}
          options={[
            { value: 'vote', label: t('resolutions.decided_by.vote') },
            { value: 'consensus', label: t('resolutions.decided_by.consensus') },
            { value: 'chair_ruling', label: t('resolutions.decided_by.chair_ruling') },
            { value: 'noted', label: t('resolutions.decided_by.noted') },
          ]}
        />
        <Checkbox
          checked={secret}
          onChange={(e) => setSecret(e.target.checked)}
          label={t('resolutions.labels.secret')}
        />
      </div>
    </Modal>
  )
}
